/**
 * StationGlobe — Three.js GLB viewer for the station panel.
 * Loads a station .glb model with orbit controls, dense starfield,
 * blinking beacon lights, and occasional shooting stars.
 */

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const STATION_MODEL_PATH = '/assets/3d/test/gateway_lunar_space_station.glb';

/** Module-level cache so the GLB is loaded only once across remounts. */
let cachedScene: THREE.Group | null = null;
let loadingPromise: Promise<THREE.Group | null> | null = null;

function loadStationModel(): Promise<THREE.Group | null> {
  if (cachedScene) return Promise.resolve(cachedScene);
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve) => {
    const loader = new GLTFLoader();
    loader.load(
      STATION_MODEL_PATH,
      (gltf) => {
        cachedScene = gltf.scene;
        resolve(cachedScene);
      },
      undefined,
      (err) => {
        console.warn('[StationGlobe] Failed to load station model:', err);
        resolve(null);
      },
    );
  });
  return loadingPromise;
}

// ==================== Shooting stars ====================

interface ShootingStar {
  line: THREE.Line;
  start: THREE.Vector3;
  direction: THREE.Vector3;
  speed: number;
  t: number;       // 0..1
  length: number;  // trail length in world units
  opacity: number;
}

const SHOOTING_STAR_POOL = 3;
const SHOOTING_STAR_INTERVAL_MIN = 2.0; // seconds between spawns
const SHOOTING_STAR_INTERVAL_MAX = 6.0;

function createShootingStar(scene: THREE.Scene): ShootingStar {
  const geo = new THREE.BufferGeometry();
  // Two points: head and tail — updated each frame
  geo.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0], 3));
  const mat = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
  });
  const line = new THREE.Line(geo, mat);
  line.frustumCulled = false;
  scene.add(line);

  return {
    line,
    start: new THREE.Vector3(),
    direction: new THREE.Vector3(),
    speed: 0,
    t: 1, // start as "done" so it waits for spawn
    length: 0,
    opacity: 0,
  };
}

function spawnShootingStar(star: ShootingStar): void {
  // Start from a random point on a distant sphere
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  const r = 30 + Math.random() * 20;
  star.start.set(
    r * Math.sin(phi) * Math.cos(theta),
    r * Math.sin(phi) * Math.sin(theta),
    r * Math.cos(phi),
  );

  // Direction: mostly tangential to the sphere, with slight inward bias
  const inward = star.start.clone().negate().normalize();
  const tangent = new THREE.Vector3(
    Math.random() - 0.5,
    Math.random() - 0.5,
    Math.random() - 0.5,
  ).normalize();
  star.direction.copy(tangent).multiplyScalar(0.85).add(inward.multiplyScalar(0.15)).normalize();

  star.speed = 15 + Math.random() * 25; // fast
  star.length = 1.5 + Math.random() * 3;
  star.t = 0;
  star.opacity = 0.4 + Math.random() * 0.5;
}

function updateShootingStar(star: ShootingStar, dt: number): boolean {
  if (star.t >= 1) return false; // inactive

  star.t += (star.speed * dt) / 60; // normalize to ~60 unit travel
  if (star.t >= 1) {
    (star.line.material as THREE.LineBasicMaterial).opacity = 0;
    return true; // done
  }

  // Head position
  const travel = star.t * 60;
  const head = star.start.clone().addScaledVector(star.direction, travel);
  const tail = head.clone().addScaledVector(star.direction, -star.length);

  const positions = star.line.geometry.attributes.position as THREE.BufferAttribute;
  positions.setXYZ(0, head.x, head.y, head.z);
  positions.setXYZ(1, tail.x, tail.y, tail.z);
  positions.needsUpdate = true;

  // Fade in then out
  const fade = star.t < 0.1
    ? star.t / 0.1
    : star.t > 0.7
      ? (1 - star.t) / 0.3
      : 1;
  (star.line.material as THREE.LineBasicMaterial).opacity = star.opacity * fade;

  return false;
}

// ==================== Blinking beacon lights ====================

interface Beacon {
  light: THREE.PointLight;
  phase: number;
}

function createBeacons(pivot: THREE.Group): Beacon[] {
  const beacons: Beacon[] = [];
  const configs: Array<{ pos: THREE.Vector3; color: number }> = [
    { pos: new THREE.Vector3(0.8, 0.4, 0), color: 0xff2020 },
    { pos: new THREE.Vector3(-0.7, 0.3, 0.2), color: 0xff2020 },
    { pos: new THREE.Vector3(0, -0.5, 0.6), color: 0x20a5a6 },
    { pos: new THREE.Vector3(0.3, 0.6, -0.4), color: 0x20a5a6 },
    { pos: new THREE.Vector3(-0.4, -0.3, -0.5), color: 0xffffff },
  ];

  for (let i = 0; i < configs.length; i++) {
    const { pos, color } = configs[i];
    const light = new THREE.PointLight(color, 0, 0.8);
    light.position.copy(pos);
    pivot.add(light);
    beacons.push({ light, phase: i * 1.3 });
  }
  return beacons;
}

function updateBeacons(beacons: Beacon[], time: number): void {
  for (const b of beacons) {
    const cycle = ((time + b.phase) % 2.0) / 2.0;
    b.light.intensity = cycle < 0.1 ? 0.6 : (cycle < 0.15 ? 0.2 : 0);
  }
}

// ==================== Component ====================

export function StationGlobe() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // ============ Scene setup ============
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x070b14);

    const camera = new THREE.PerspectiveCamera(
      45,
      container.clientWidth / container.clientHeight,
      0.01,
      500,
    );
    camera.position.set(0, 0, 4);
    camera.lookAt(0, 0, 0);
    let zoomTarget = 4;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    container.appendChild(renderer.domElement);

    // ============ Starfield (dense, colored) ============
    {
      const starCount = 2500;
      const positions = new Float32Array(starCount * 3);
      const colors = new Float32Array(starCount * 3);
      for (let i = 0; i < starCount; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const r = 60 + Math.random() * 40;
        positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
        positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        positions[i * 3 + 2] = r * Math.cos(phi);

        const warmth = Math.random();
        if (warmth < 0.7) {
          colors[i * 3] = 1; colors[i * 3 + 1] = 1; colors[i * 3 + 2] = 1;
        } else if (warmth < 0.85) {
          colors[i * 3] = 0.7; colors[i * 3 + 1] = 0.8; colors[i * 3 + 2] = 1;
        } else {
          colors[i * 3] = 1; colors[i * 3 + 1] = 0.9; colors[i * 3 + 2] = 0.7;
        }
      }
      const starGeo = new THREE.BufferGeometry();
      starGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      starGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      const starMat = new THREE.PointsMaterial({
        size: 0.12,
        sizeAttenuation: true,
        vertexColors: true,
      });
      scene.add(new THREE.Points(starGeo, starMat));
    }

    // ============ Lights ============
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.0);
    keyLight.position.set(5, 3, 5);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0x4488aa, 0.3);
    fillLight.position.set(-3, -1, -3);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0x20a5a6, 0.4);
    rimLight.position.set(0, -2, -4);
    scene.add(rimLight);

    // ============ Model pivot ============
    const pivot = new THREE.Group();
    scene.add(pivot);

    let beacons: Beacon[] = [];

    loadStationModel().then((template) => {
      if (!template || disposed) return;
      const model = template.clone(true);

      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 2.0 / maxDim;

      model.position.copy(center).negate().multiplyScalar(scale);
      model.scale.setScalar(scale);

      pivot.add(model);
      beacons = createBeacons(pivot);
    });

    // ============ Shooting stars ============
    const shootingStars: ShootingStar[] = [];
    for (let i = 0; i < SHOOTING_STAR_POOL; i++) {
      shootingStars.push(createShootingStar(scene));
    }
    let nextSpawnIn = 1 + Math.random() * 2; // first one comes quickly

    // ============ Interaction ============
    let isDragging = false;
    let lastX = 0;
    let lastY = 0;

    const onPointerDown = (e: PointerEvent) => {
      isDragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;

      const dist = zoomTarget;
      const surfaceDist = Math.max(0.01, dist - 1.0);
      const dragSpeed = surfaceDist * 0.003;
      pivot.rotation.y += dx * dragSpeed;
      pivot.rotation.x = Math.max(
        -Math.PI / 2 + 0.01,
        Math.min(Math.PI / 2 - 0.01, pivot.rotation.x + dy * dragSpeed),
      );
    };

    const onPointerUp = (e: PointerEvent) => {
      isDragging = false;
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch { /* noop */ }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomSpeed = zoomTarget * 0.0008;
      zoomTarget = Math.max(1.5, Math.min(12, zoomTarget + e.deltaY * zoomSpeed));
    };

    const canvas = renderer.domElement;
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    // ============ Resize ============
    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const resizeObs = new ResizeObserver(onResize);
    resizeObs.observe(container);

    // ============ Render loop ============
    let disposed = false;
    let lastTime = performance.now();
    let elapsedTime = 0;
    let animId = 0;

    const animate = () => {
      if (disposed) return;
      animId = requestAnimationFrame(animate);

      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;
      elapsedTime += dt;

      // Smooth zoom
      const dist = camera.position.length();
      if (Math.abs(dist - zoomTarget) > 0.001) {
        const newDist = dist + (zoomTarget - dist) * Math.min(1, dt * 8);
        camera.position.normalize().multiplyScalar(newDist);
      }

      // Update shooting stars
      nextSpawnIn -= dt;
      if (nextSpawnIn <= 0) {
        // Find an idle star to spawn
        const idle = shootingStars.find((s) => s.t >= 1);
        if (idle) spawnShootingStar(idle);
        nextSpawnIn = SHOOTING_STAR_INTERVAL_MIN +
          Math.random() * (SHOOTING_STAR_INTERVAL_MAX - SHOOTING_STAR_INTERVAL_MIN);
      }
      for (const star of shootingStars) {
        updateShootingStar(star, dt);
      }

      // Update beacons
      if (beacons.length > 0) {
        updateBeacons(beacons, elapsedTime);
      }

      renderer.render(scene, camera);
    };
    animate();

    // ============ Cleanup ============
    return () => {
      disposed = true;
      cancelAnimationFrame(animId);
      resizeObs.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);
      for (const star of shootingStars) {
        scene.remove(star.line);
        star.line.geometry.dispose();
        (star.line.material as THREE.Material).dispose();
      }
      renderer.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', cursor: 'grab' }}
    />
  );
}
